// Onboarding/offboarding task board (Lifecycle L2) shown on the employee
// profile while an instance is in progress. Tasks are grouped into lanes by
// assignee role; computed tasks mirror live platform state (readiness,
// documents, payroll) and can't be ticked manually — only unverifiable steps
// get Complete/Reopen, recording a named confirmer.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, CheckCircle2, Circle, ClipboardList, ExternalLink, Loader2, Ban, Check, RotateCcw, Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Alert, AlertDescription } from '@/features/shared/components/ui/alert';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Progress } from '@/features/shared/components/ui/progress';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useHasPermission, useHasTenantWidePermission } from '@/rbac/useRbac';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  completeLifecycleInstance, cancelLifecycleInstance, completeLifecycleTask, reopenLifecycleTask,
  ASSIGNEE_ROLE_ORDER, assigneeRoleLabel, exitTypeLabel,
  type LifecycleInstanceView, type LifecycleTaskState,
} from '@/services/lifecycleService';
import { getAsanaLifecycleLinks, type AsanaLifecycleLinks, type AsanaTaskLink } from '@/services/developerService';

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

/** Date-only fields (YYYY-MM-DD) — parse at noon UTC to dodge TZ day shifts. */
const fmtDate = (d?: string | null) =>
  d ? new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

// Chip for a task whose execution surface is Asana (delegated). The normal
// KaziPeople Complete/Reopen controls are suppressed for these — one task, one
// interaction surface (Asana V1 UX rule).
const AsanaTaskChip: React.FC<{ done: boolean; link?: AsanaTaskLink }> = ({ done, link }) => {
  const { t } = useTranslation('employees');
  const label = done
    ? t('lifecycle.asana_done', { defaultValue: 'Completed via Asana' })
    : link
      ? t('lifecycle.asana_in_progress', { defaultValue: 'In Asana' })
      : t('lifecycle.asana_pending_sync', { defaultValue: 'Waiting to sync to Asana' });
  const cls = done
    ? 'bg-success-soft text-success-strong border-transparent'
    : 'bg-muted text-muted-foreground border-border';
  return (
    <span className="inline-flex items-center gap-1.5 mt-1">
      <Badge className={`${cls} font-normal`}>{label}</Badge>
      {link?.url && (
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-0.5 text-xs text-orange-600 dark:text-orange-400 hover:underline"
        >
          {t('lifecycle.asana_open', { defaultValue: 'Open in Asana' })}
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </span>
  );
};

// ------------------------------------------------------------------ task row
const TaskRow: React.FC<{
  task: LifecycleTaskState;
  canAct: boolean;
  /** Task execution is delegated to Asana — suppress local completion. */
  asanaDelegated?: boolean;
  asanaLink?: AsanaTaskLink;
  onChanged: () => void;
}> = ({ task, canAct, asanaDelegated, asanaLink, onChanged }) => {
  const { t } = useTranslation('employees');
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const doComplete = async () => {
    setBusy(true);
    try {
      await completeLifecycleTask(task.id, note);
      toast.success(t('lifecycle.task_completed', { defaultValue: '“{{title}}” marked done.', title: task.title }));
      setNoteOpen(false);
      setNote('');
      onChanged();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('lifecycle.task_action_failed', { defaultValue: 'Could not update the task.' }))));
    } finally {
      setBusy(false);
    }
  };

  const doReopen = async () => {
    setBusy(true);
    try {
      await reopenLifecycleTask(task.id);
      toast.success(t('lifecycle.task_reopened', { defaultValue: '“{{title}}” reopened.', title: task.title }));
      onChanged();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('lifecycle.task_action_failed', { defaultValue: 'Could not update the task.' }))));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="py-2.5 px-3 rounded-lg border border-border bg-card">
      <div className="flex items-start gap-2.5">
        {task.done ? (
          <CheckCircle2 className="w-[18px] h-[18px] text-success mt-0.5 shrink-0" />
        ) : (
          <Circle className="w-[18px] h-[18px] text-muted-foreground/50 mt-0.5 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm ${task.done ? 'text-muted-foreground line-through decoration-border' : 'text-foreground'}`}>
              {task.title}
            </span>
            {!task.required && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded-full px-1.5 py-px">
                {t('lifecycle.optional', { defaultValue: 'Optional' })}
              </span>
            )}
          </div>

          {/* Asana-delegated tasks: Asana is the interaction surface — status
              chip + deep link here, never a second Mark-done. */}
          {!task.computed && asanaDelegated ? (
            <>
              <AsanaTaskChip done={task.done} link={asanaLink} />
              {task.done && task.completed_at && (
                <p className="text-[11px] text-muted-foreground mt-0.5">{fmtDateTime(task.completed_at)}</p>
              )}
            </>
          ) : task.computed ? (
            <>
              {!task.done && task.detail && (
                <p className="text-xs text-warning mt-1">{task.detail}</p>
              )}
              {!task.done && (
                <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {t('lifecycle.auto_hint', { defaultValue: 'Completes automatically when the data is in.' })}
                </p>
              )}
            </>
          ) : (
            <>
              {task.done && (task.completed_by || task.note) && (
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {task.completed_by
                    ? t('lifecycle.confirmed_by', { defaultValue: 'Confirmed by {{name}}', name: task.completed_by })
                    : null}
                  {task.completed_at ? ` · ${fmtDateTime(task.completed_at)}` : ''}
                  {task.note ? ` · “${task.note}”` : ''}
                </p>
              )}
              {canAct && (
                <div className="mt-1.5">
                  {task.done ? (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground" onClick={doReopen} disabled={busy}>
                      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                      {t('lifecycle.reopen', { defaultValue: 'Reopen' })}
                    </Button>
                  ) : noteOpen ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        className="h-8 px-2 rounded-md border border-border bg-card text-xs flex-1 min-w-[180px]"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={t('lifecycle.note_ph', { defaultValue: 'Note (optional)' })}
                        aria-label={t('lifecycle.note_ph', { defaultValue: 'Note (optional)' })}
                        autoFocus
                      />
                      <Button size="sm" className="h-8 px-3 text-xs" onClick={doComplete} disabled={busy}>
                        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                        {t('lifecycle.mark_done', { defaultValue: 'Mark done' })}
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 px-3 text-xs" onClick={() => { setNoteOpen(false); setNote(''); }} disabled={busy}>
                        {t('common.cancel', { defaultValue: 'Cancel' })}
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => setNoteOpen(true)} disabled={busy}>
                      <Check className="w-3 h-3 mr-1" />
                      {t('lifecycle.complete_task', { defaultValue: 'Complete' })}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------- card
export const EmployeeLifecycleCard: React.FC<{
  instance: LifecycleInstanceView;
  /** Refetch just the instance (task state changed). */
  onChanged: () => void;
  /** Instance finished or was cancelled — refetch employee + instance. */
  onCompleted: () => void;
}> = ({ instance, onChanged, onCompleted }) => {
  const { t } = useTranslation('employees');
  const confirm = useConfirm();
  const { isManager } = useRoleAccess();
  // Completing a package is a company-wide approval: line managers hold
  // employee:change:approve only at TEAM scope (their reports' change requests).
  const canApprove = useHasTenantWidePermission('employee:change:approve');
  const canWrite = useHasPermission('employee:write');
  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Asana delegation + link state for this instance. Best-effort: any failure
  // (integration absent, not rolled out) renders the plain KaziPeople board.
  const [asana, setAsana] = useState<AsanaLifecycleLinks | null>(null);
  useEffect(() => {
    let alive = true;
    const taskIds = (instance.tasks || []).map((task) => task.id);
    getAsanaLifecycleLinks(instance.id, instance.type, instance.package_name, taskIds)
      .then((links) => { if (alive) setAsana(links.connected ? links : null); })
      .catch(() => { if (alive) setAsana(null); });
    return () => { alive = false; };
  }, [instance.id, instance.type, instance.package_name, instance.tasks]);
  const delegatedKeys = useMemo(() => new Set(asana?.delegated_task_keys || []), [asana]);

  const isOffboarding = instance.type === 'offboarding';
  const flowLabel = isOffboarding
    ? t('lifecycle.offboarding', { defaultValue: 'Offboarding' })
    : t('lifecycle.onboarding', { defaultValue: 'Onboarding' });

  const lanes = useMemo(() => {
    const byRole = new Map<string, LifecycleTaskState[]>();
    for (const task of instance.tasks || []) {
      const role = task.assignee_role || 'hr';
      if (!byRole.has(role)) byRole.set(role, []);
      byRole.get(role)!.push(task);
    }
    const ordered: { role: string; tasks: LifecycleTaskState[] }[] = [];
    for (const role of ASSIGNEE_ROLE_ORDER) {
      const tasks = byRole.get(role);
      if (tasks?.length) { ordered.push({ role, tasks }); byRole.delete(role); }
    }
    // Any unexpected roles go last (forward-compatible).
    byRole.forEach((tasks, role) => ordered.push({ role, tasks }));
    return ordered;
  }, [instance.tasks]);

  const progress = instance.progress;
  const pct = progress.required_total > 0
    ? Math.round((progress.required_done / progress.required_total) * 100)
    : (progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0);

  const completeInstance = async () => {
    const ok = await confirm({
      title: t('lifecycle.complete_title', { defaultValue: 'Complete {{flow}}', flow: flowLabel.toLowerCase() }),
      message: isOffboarding
        ? t('lifecycle.complete_confirm_off', {
            defaultValue: '{{name}} will become Terminated and payroll will be disabled. Continue?',
            name: instance.employee?.name,
          })
        : t('lifecycle.complete_confirm_on', {
            defaultValue: '{{name}} will become Active and payroll will be enabled. Continue?',
            name: instance.employee?.name,
          }),
      confirmLabel: t('lifecycle.complete_action', { defaultValue: 'Complete {{flow}}', flow: flowLabel.toLowerCase() }),
    });
    if (!ok) return;
    setCompleting(true);
    try {
      const res = await completeLifecycleInstance(instance.id);
      const base = isOffboarding && res.engagement_note
        ? res.engagement_note
        : isOffboarding
        ? t('lifecycle.completed_off', { defaultValue: 'Offboarding complete — employee is now {{status}}.', status: res.employee_status })
        : t('lifecycle.completed_on', { defaultValue: 'Onboarding complete — employee is now {{status}}.', status: res.employee_status });
      toast.success(res.payroll_note ? `${base} ${res.payroll_note}` : base, { duration: 6000 });
      onCompleted();
    } catch (e: any) {
      // 409 lists the pending required tasks.
      toast.error(getApiErrorMessage(e, errMsg(e, t('lifecycle.complete_failed', { defaultValue: 'Could not complete the checklist.' }))));
      if (e?.response?.status === 409) onChanged();
    } finally {
      setCompleting(false);
    }
  };

  const cancelInstance = async () => {
    const ok = await confirm({
      title: t('lifecycle.cancel_title', { defaultValue: 'Cancel {{flow}}', flow: flowLabel.toLowerCase() }),
      message: t('lifecycle.cancel_confirm', {
        defaultValue: 'Cancel this {{flow}} checklist for {{name}}? Task progress will be discarded.',
        flow: flowLabel.toLowerCase(),
        name: instance.employee?.name,
      }),
      confirmLabel: t('lifecycle.cancel_action', { defaultValue: 'Cancel checklist' }),
      danger: true,
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await cancelLifecycleInstance(instance.id);
      toast.success(t('lifecycle.cancelled_toast', { defaultValue: '{{flow}} cancelled.', flow: flowLabel }));
      onCompleted();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('lifecycle.cancel_failed', { defaultValue: 'Could not cancel the checklist.' }))));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle className="text-base text-orange-700 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> {flowLabel}
            <Badge className="border bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-500/25 font-normal">
              {instance.package_name}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-3">
            {asana?.instance_url && (
              <a
                href={asana.instance_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 hover:underline"
              >
                {t('lifecycle.asana_open', { defaultValue: 'Open in Asana' })}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {instance.started_at && (
              <span className="text-xs text-muted-foreground">
                {t('lifecycle.started', { defaultValue: 'Started' })} {fmtDateTime(instance.started_at)}
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Exit summary strip (offboarding only) */}
        {isOffboarding && (
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
            <span className="text-muted-foreground">
              {t('lifecycle.exit_type', { defaultValue: 'Exit type' })}{': '}
              <span className="text-foreground font-medium">
                {instance.exit_type ? exitTypeLabel(t, instance.exit_type) : '—'}
              </span>
            </span>
            <span className="text-muted-foreground">
              {t('lifecycle.last_working_day', { defaultValue: 'Last working day' })}{': '}
              <span className="text-foreground font-medium">{fmtDate(instance.last_working_date)}</span>
            </span>
            <span className="text-muted-foreground">
              {t('lifecycle.final_payroll_date', { defaultValue: 'Final payroll date' })}{': '}
              <span className="text-foreground font-medium">{fmtDate(instance.final_payroll_date)}</span>
            </span>
            {instance.reason && (
              <span className="text-muted-foreground min-w-0 max-w-full truncate" title={instance.reason}>
                {t('lifecycle.exit_reason_label', { defaultValue: 'Reason' })}{': '}
                <span className="text-foreground">{instance.reason}</span>
              </span>
            )}
          </div>
        )}

        {/* Overdue exit: the final payroll date is behind us but the checklist is open */}
        {isOffboarding && instance.overdue && (
          <Alert variant="destructive" className="border-danger bg-danger-soft text-danger-strong">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t('lifecycle.overdue_banner', {
                defaultValue: 'The final payroll date has passed but this exit is still open — finish the checklist or fix the date.',
              })}
            </AlertDescription>
          </Alert>
        )}

        {/* Progress: required tasks are what gates completion */}
        <div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span>
              {t('lifecycle.progress_required', {
                defaultValue: '{{done}} of {{total}} required tasks done',
                done: progress.required_done,
                total: progress.required_total,
              })}
            </span>
            <span>{pct}%</span>
          </div>
          <Progress value={pct} className="h-2 bg-orange-100 dark:bg-orange-500/[0.12] [&>div]:bg-gradient-to-r [&>div]:from-orange-500 [&>div]:to-amber-500" />
        </div>

        {/* Lanes by assignee role */}
        <div className="space-y-4">
          {lanes.map(({ role, tasks }) => (
            <div key={role}>
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.1em] mb-1.5">
                {assigneeRoleLabel(role)}
              </p>
              <div className="space-y-2">
                {tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    canAct={isManager}
                    asanaDelegated={delegatedKeys.has(task.key)}
                    asanaLink={asana?.tasks?.[task.id]}
                    onChanged={onChanged}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer actions */}
        {(canApprove || canWrite) && (
          <div className="pt-2 border-t border-border flex items-center gap-2 flex-wrap">
            {canApprove && (
              <Button onClick={completeInstance} disabled={!progress.ready_to_complete || completing || cancelling}>
                {completing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                {t('lifecycle.complete_action', { defaultValue: 'Complete {{flow}}', flow: flowLabel.toLowerCase() })}
              </Button>
            )}
            {canApprove && !progress.ready_to_complete && (
              <span className="text-xs text-muted-foreground">
                {t('lifecycle.not_ready_hint', { defaultValue: 'All required tasks must be done first.' })}
              </span>
            )}
            {canWrite && (
              <Button variant="ghost" className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 ml-auto" onClick={cancelInstance} disabled={completing || cancelling}>
                {cancelling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                {t('lifecycle.cancel_action', { defaultValue: 'Cancel checklist' })}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeLifecycleCard;
